<template>
  <div class="order-creator">
    <template v-if="loadError">
      <d-alert
        show
        theme="warning">
        Wczytywanie danych nie powiodło się.
        <a
          href="javascript:window.location.reload();"
          class="alert-link">
          Odśwież stronę
        </a>
        aby spróbować ponownie.
      </d-alert>
    </template>
    <template v-else-if="!dataLoaded">
      <div class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>
    </template>
    <template v-else>
      <template v-if="isCustomerNotExisitng">
        <d-alert
          theme="danger"
          show>
          Klient przypisany do zamówienia nie został znaleziony w bazie danych.
        </d-alert>
      </template>
      <template v-else-if="!customerID">
        <d-alert
          theme="warning"
          show>
          Produkty można dodawać po wybraniu klienta.
        </d-alert>
      </template>
      <template v-else-if="customerID && !products.length">
        <d-alert
          theme="warning"
          show>
          Dodaj produkty do zamówienia klikając przycisk "Dodaj produkt"
        </d-alert>
      </template>

      <div
        v-if="dataLoaded && pricesRecalculated && loadedCustomerProducts">
        <order-creator-product
          v-for="(product, index) of products"
          :key="'order-creator-product-' + index"
          :ref="'order-creator-product-' + index"
          :all-products="allProducts"
          :customer-id="customerID"
          :initial-customer-products="allCustomerProducts.length ? allCustomerProducts.slice(0, 20) : []"
          :is-disabled="disabled"
          :is-worker="isWorker"
          :is-duplicated="multipleSameProducts.length && (multipleSameProducts.indexOf(index) > -1)"
          :order-form="orderForm"
          :product="product"
          :product-index="index"
          :is-edit="isEdit" />
      </div>

      <div
        v-if="products.length > 0"
        class="text-right mt-3">
        <d-button
          :disabled="addBtnDisabled"
          theme="success"
          class="ml-auto"
          @click.prevent="addProduct">
          Dodaj produkt
        </d-button>
      </div>
    </template>

    <order-creator-summary
      :isVisible="products.length && dataLoaded && !loadError"
      :orderProducts="products"
      :productDefaultPricesAndTaxes="productDefaultPricesAndTaxes" />

    <d-button
      v-if="dataLoaded && !loadError"
      :disabled="disabled"
      theme="success"
      class="d-block ml-auto mt-5"
      @click.prevent="$bus.$emit('validate-order-form')">
      Zapisz zmiany
    </d-button>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import CustomDecimals from '@/utils/CustomDecimals.js';
import OrderCreatorSummary from './OrderCreatorSummary.vue';
import OrderCreatorProduct from './OrderCreatorProduct.vue';

export default {
  name: 'order-creator',
  props: [
    'addBtnDisabled',
    'disabled',
    'isEdit',
    'allProducts',
    'orderForm',
    'isCustomerNotExisitng'
  ],
  components: {
    'order-creator-summary': OrderCreatorSummary,
    'order-creator-product': OrderCreatorProduct
  },
  computed: {
    formErrors () {
      return this.orderForm.errors;
    },
    productsWithVariants () {
      return this.allCustomerProducts.filter(product => product.children.length).map(product => product.id);
    },
    productTotals () {
      return this.products.slice().map(product => ({
        price: this.roundProperly(product.price * product.quantity),
        price_before_discount: this.roundProperly(product.price_before_discount * product.quantity)
      }));
    },
    productDefaultPricesAndTaxes () {
      let productsIndex = {};

      for (let i = 0; i < this.allCustomerProducts.length; i++) {
        let product = this.allCustomerProducts[i];

        productsIndex[product.id] = {
          price: product.price,
          vatRate: product.vat_rate,
          discount: parseFloat(product.discount)
        };

        if (product.type !== 'VARIANT') {
          productsIndex[product.id].available_in_package = product.available_in_package;
          productsIndex[product.id].package_price = product.package_price;
          productsIndex[product.id].quantity_in_package = product.quantity_in_package
        }

        if (product.children.length) {
          for (let j = 0; j < product.children.length; j++) {
            let child = product.children[j];

            productsIndex[child.id] = {
              price: child.price,
              vatRate: child.vat_rate,
              discount: parseFloat(child.discount),
              available_in_package: child.available_in_package,
              package_price: child.package_price,
              quantity_in_package: child.quantity_in_package
            };
          }
        }
      }

      return productsIndex;
    },
    multipleSameProducts () {
      if (!this.products.length) {
        return [];
      }

      let multipleSameIndexs = [];
      for (let i = 0; i < this.products.length; i++) {
        let productID = this.products[i].product_id;
        let productVariantID = this.products[i].variant_product_id;
        if (productID && productVariantID) {
          let otherSame = this.products.filter(item => item.product_id === productID && item.variant_product_id === productVariantID);

          if (otherSame.length > 1) {
            multipleSameIndexs.push(i);
          }
        }
      }

      return multipleSameIndexs;
    },
    isWorker () {
      return this.$store.getters['getUserType'] === 'WORKER';
    }
  },
  data () {
    return {
      customerID: null,
      products: [],
      allCustomerProducts: [],
      loadedCustomerProducts: false,
      dataLoaded: false,
      loadError: false,
      pricesRecalculated: false,
      variantsEmptyResults: false,
      productsEmptyResults: false,
      initialCustomerProducts: []
    };
  },
  mounted () {
    this.$bus.$on('order-update-client-discounts', this.recalculateDiscount);
  },
  methods: {
    getAllProductsChildren () {
      return this.allProducts.filter(product => product.type === 'VARIANT').map(product => product.children.map(child => child.id).join(';')).join(';').split(';')
    },
    getAllProductsRelations () {
      return this.allProducts.filter(product => product.type === 'VARIANT').map(product => ({
        parent_id: product.id,
        children_ids: product.children.map(child => child.id)
      }));
    },
    addProduct () {
      this.products.push({
        id: null,
        product_id: null,
        variant_product_id: null,
        price: 0,
        price_before_discount: '',
        vat_rate: null,
        quantity: null,
        discount: 0,
        available_in_package: false,
        quantity_in_package: 0,
        is_package: false,
        stock: 0
      });
    },
    removeProduct (productIndex) {
      this.products.splice(productIndex, 1);
      Vue.nextTick(() => {
        this.$bus.$emit('order-products-reset', this.resetProduct);
      });
    },
    recalculateDiscount (customerID = null) {
      this.$bus.$emit('order-client-discounts-loading', false);
      Vue.set(this, 'dataLoaded', false);
      this.pricesRecalculated = false;

      if (this.isEdit) {
        this.allCustomerProducts = this.allProducts;

        Vue.nextTick(() => {
          this.dataLoaded = true;
          this.customerID = customerID;
          this.loadedCustomerProducts = true;
          this.pricesRecalculated = true;
          this.$bus.$emit('order-client-discounts-updated', true);
        });
      } else if (customerID && customerID !== this.customerID) {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/api/products/all-items?customer_id=' + customerID,
          method: 'get',
          outputKey: 'allCustomerProducts',
          loadedKey: 'loadedCustomerProducts',
          errorKey: 'loadError',
          successAction: () => {
            Vue.nextTick(() => {
              this.dataLoaded = true;
              this.customerID = customerID;
              this.pricesRecalculated = true;
              this.$bus.$emit('order-client-discounts-updated', true);
            });
          }
        });
      } else {
        this.dataLoaded = true;
        this.customerID = customerID;
        this.pricesRecalculated = true;
        this.$bus.$emit('order-client-discounts-updated', true);
      }
    },
    getProducts () {
      let productsToSend = [];
      for (let i = 0; i < this.products.length; i++) {
        let orderProductID = this.products[i].product_id;
        if (this.products[i].variant_product_id) {
          orderProductID = this.products[i].variant_product_id;
        }
        productsToSend.push({
          id: this.isEdit ? this.products[i].id : 0,
          product_id: orderProductID,
          price: parseFloat(this.products[i].price),
          price_before_discount: parseFloat(this.products[i].price_before_discount),
          vat_rate: this.products[i].vat_rate,
          quantity: this.products[i].quantity,
          discount: this.products[i].discount,
          is_package: this.products[i].is_package ? 1 : 0
        });

        if (this.products[i].is_package) {
          let quantityInPackage = Number(this.productDefaultPricesAndTaxes[orderProductID].quantity_in_package);
          productsToSend[i].price = parseFloat(this.products[i].price / quantityInPackage);
          productsToSend[i].price_before_discount = parseFloat(this.products[i].price_before_discount / quantityInPackage);
          productsToSend[i].quantity = this.products[i].quantity * quantityInPackage;
          productsToSend[i].package_price = parseFloat(this.products[i].price);
          productsToSend[i].package_price_before_discount = parseFloat(this.products[i].price_before_discount);
          productsToSend[i].package_quantity = Number(this.products[i].quantity);
        }
      }

      return productsToSend;
    },
    setUpProducts (orderProducts) {
      let allAvailableProductsVarians = this.getAllProductsChildren();
      let relations = this.getAllProductsRelations();
      let finalRelations = {};
      for (let i = 0; i < relations.length; i++) {
        for (let j = 0; j < relations[i].children_ids.length; j++) {
          let parentID = relations[i].parent_id;
          let childID = relations[i].children_ids[j];
          finalRelations[childID] = parentID;
        }
      }
      for (let i = 0; i < orderProducts.length; i++) {
        let product = orderProducts[i];
        let parentID = product.product_id;
        let productVariantID = null;

        if (allAvailableProductsVarians.indexOf((product.product_id).toString()) > -1) {
          parentID = finalRelations[product.product_id];
          productVariantID = product.product_id
        }

        this.products.push({
          id: product.id,
          product_id: parentID,
          price: product.price,
          price_before_discount: product.price_before_discount,
          vat_rate: product.vat_rate,
          quantity: product.quantity,
          variant_product_id: productVariantID,
          discount: parseFloat(product.discount),
          name: product.name,
          variant_name: product.variant_name,
          is_package: !!product.is_package,
          package_price_before_discount: product.package_price_before_discount,
          package_price: product.package_price,
          package_quantity: product.package_quantity,
          available_in_package: product.available_in_package
        });

        if (this.products[i].is_package) {
          let quantityInPackage = Number(product.quantity_in_package);
          this.products[i].price = parseFloat(product.price * quantityInPackage);
          this.products[i].price_before_discount = parseFloat(product.price_before_discount * quantityInPackage);
          this.products[i].quantity = Number(product.quantity) / quantityInPackage;
        }
      }

      this.dataLoaded = true;
    },
    roundProperly (value, decimals = 2) {
      return CustomDecimals.roundProperly(value, decimals);
    },
    getProduct (productID) {
      if (this.allCustomerProducts.length) {
        return this.allCustomerProducts.find(item => item.id === productID);
      }

      return null;
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-update-client-discounts', this.recalculateDiscount);
  }
};
</script>

<style lang="scss">
.product-info {
  &-select {
    min-width: 230px;
  }
}

@media screen and (max-width: 1200px) {
  .product-info {
    &-pricing {
      flex-wrap: wrap!important;

      > div {
        margin-left: 1rem !important;
        min-width: 55%;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .product-info {
    &-select {
      width: 100%!important;
    }
    &-pricing {
      margin-top: 20px;
      width: 100%!important;

      > div {
        margin-left: 0!important;
        margin-right: 1rem!important;
        min-width: unset;
      }
    }
  }
}
</style>
